/**
 * Out-of-band trust root for verification packs (B3).
 *
 * A pack ships its own verifier and its own signing keys, so a pack that
 * validates itself proves only that its author was self-consistent. Anchoring
 * requires key material the pack cannot author: a fingerprint the customer
 * obtained from somewhere else.
 */
export const OUT_OF_BAND_KEY_SOURCES = [
    "well-known-url",
    "in-product-runtime-page",
    "operator-pinned",
];
/**
 * The ceiling on what anchoring can prove, stated rather than implied.
 *
 * Receipts signed with legacy HMAC-SHA256 are symmetric: the "fingerprint" a
 * customer holds out of band is a fingerprint over a SHARED SECRET. Anchoring
 * proves the pack was signed with the key that customer recognizes, but it
 * cannot prove the vendor did not mint the pack. Ed25519 changes that ceiling:
 * an anchored pack verifies against a public key while the private key stays in
 * customer custody.
 */
export function packTrustRootAssuranceCeiling(mode, signatureAlgorithm = "hmac-sha256") {
    if (mode === "anchored" && signatureAlgorithm === "ed25519") {
        return {
            independentOfVendor: true,
            statement: "This pack was signed with an Ed25519 private key and verified against a public key you hold independently. Excellent can verify the signature, but it cannot mint a matching pack without the customer-held private key.",
        };
    }
    if (mode === "anchored") {
        return {
            independentOfVendor: false,
            statement: "This pack was signed with the key you hold independently, which rules out a substituted signing key. It does not rule out a pack minted by Excellent: legacy HMAC receipt signing is symmetric, so the key you verify against is a shared secret rather than a public key.",
        };
    }
    // NOT ANCHORED. This branch is rendered VERBATIM to users, and it previously returned the
    // legacy-HMAC sentence for every unanchored mode — including `ed25519`. The refusal was
    // right and the cause it named was wrong, which is worse than a generic explanation
    // because it sends the reader to the wrong fix: they would go looking for a signing
    // algorithm to upgrade, when the algorithm is already asymmetric and the missing thing is
    // an out-of-band fingerprint for the key. (D2/L8, 2026-09-12, from L9's §6.12.5 finding.)
    if (signatureAlgorithm === "ed25519") {
        return {
            independentOfVendor: false,
            statement: "This pack was signed with an Ed25519 private key, so its signature is asymmetric and offline-verifiable. What is missing is not the algorithm but the anchor: no fingerprint for the signing key was obtained out of band, so the key was checked only against keys travelling inside the pack. Verification here establishes internal consistency and key recognition, not independence from Excellent. Obtain the key's fingerprint from a source outside the pack to reach full assurance.",
        };
    }
    return {
        independentOfVendor: false,
        statement: "Two things are missing here, and both matter. No fingerprint for the signing key was obtained out of band, so the key was checked only against keys travelling inside the pack; and legacy HMAC receipt signing is symmetric, so the key you verify against is a shared secret rather than a public key. Verification here establishes internal consistency and key recognition, not independence from Excellent.",
    };
}
function normalizeFingerprint(value) {
    return typeof value === "string" ? value.trim().toLowerCase() : "";
}
function hasText(value) {
    return typeof value === "string" && value.trim().length > 0;
}
function findingSeverity(findings, severity) {
    return findings.some((finding) => finding.severity === severity);
}
export function verifyPackTrustRoot(input) {
    const findings = [];
    const signingKeyId = input.pack.signedByKeyId;
    const declared = input.pack.declaredKeys.find((key) => key.keyId === signingKeyId) ?? null;
    const declaredSignatureAlgorithm = declared?.algorithm ?? (declared == null ? null : "hmac-sha256");
    const packSignatureAlgorithm = input.pack.signatureAlgorithm ?? null;
    const signatureAlgorithm = declaredSignatureAlgorithm ?? packSignatureAlgorithm ?? "hmac-sha256";
    if (declared == null) {
        findings.push({
            code: "trust-root-signing-key-undeclared",
            severity: "blocking",
            keyId: signingKeyId,
            message: `This pack is signed by key ${signingKeyId}, which the pack does not declare. It was rejected because the signing key cannot be identified at all.`,
        });
    }
    else if (packSignatureAlgorithm != null &&
        declaredSignatureAlgorithm != null &&
        packSignatureAlgorithm !== declaredSignatureAlgorithm) {
        findings.push({
            code: "trust-root-signature-algorithm-mismatch",
            severity: "blocking",
            keyId: signingKeyId,
            message: `This pack was rejected: its pack-level signature algorithm is ${packSignatureAlgorithm}, but signing key ${signingKeyId} is declared as ${declaredSignatureAlgorithm}. A pack cannot upgrade a legacy HMAC signing key into an Ed25519 key by self-declaration.`,
        });
    }
    const outOfBand = input.outOfBandKeys.find((key) => key.keyId === signingKeyId) ?? null;
    let usedKeySource = null;
    if (outOfBand != null && hasText(outOfBand.revokedAt)) {
        findings.push({
            code: "trust-root-key-revoked",
            severity: "blocking",
            keyId: signingKeyId,
            message: `This pack is signed by key ${signingKeyId}, which was revoked on ${outOfBand.revokedAt}. It was rejected because a revoked key cannot anchor trust, even though the pack is internally consistent.`,
        });
    }
    else if (outOfBand != null && declared != null) {
        const expected = normalizeFingerprint(outOfBand.fingerprint);
        const claimed = normalizeFingerprint(declared.fingerprint);
        if (expected !== claimed) {
            findings.push({
                code: "trust-root-fingerprint-mismatch",
                severity: "blocking",
                keyId: signingKeyId,
                message: `This pack was rejected: its in-pack fingerprint for key ${signingKeyId} is ${declared.fingerprint}, but the fingerprint obtained out of band from ${outOfBand.source} is ${outOfBand.fingerprint}. A pack cannot vouch for its own signing key.`,
            });
        }
        else if (!findingSeverity(findings, "blocking")) {
            usedKeySource = outOfBand.source;
            findings.push({
                code: "trust-root-anchored",
                severity: "info",
                keyId: signingKeyId,
                message: `Signing key ${signingKeyId} matches the fingerprint obtained out of band from ${outOfBand.source}, so this pack's signature is anchored outside the pack.`,
            });
        }
    }
    else if (declared != null) {
        usedKeySource = "in-pack";
        findings.push({
            code: "trust-root-keys-in-pack-only",
            severity: "warning",
            keyId: signingKeyId,
            message: `No out-of-band fingerprint was available for signing key ${signingKeyId}, so this pack was checked only against keys that travel inside it. It is internally consistent but not independently anchored.`,
        });
    }
    const verifierExpected = normalizeFingerprint(input.verifierProvenance.expectedFingerprint);
    const verifierActual = normalizeFingerprint(input.verifierProvenance.fingerprint);
    if (hasText(input.verifierProvenance.expectedFingerprint) && verifierExpected !== verifierActual) {
        findings.push({
            code: "trust-root-verifier-mismatch",
            severity: "blocking",
            keyId: null,
            message: `This pack was rejected: the verifier ${input.verifierProvenance.verifierId} has fingerprint ${input.verifierProvenance.fingerprint}, but the fingerprint held out of band is ${input.verifierProvenance.expectedFingerprint}. The tool reading the evidence is not the tool that was vouched for.`,
        });
    }
    else if (input.verifierProvenance.origin === "in-pack" && !hasText(input.verifierProvenance.expectedFingerprint)) {
        findings.push({
            code: "trust-root-verifier-self-attested",
            severity: "blocking",
            keyId: null,
            message: `This pack was rejected: the verifier ${input.verifierProvenance.verifierId} was read from inside this pack and no independent fingerprint was held for it, so the pack supplied both the evidence and the tool that checked it.`,
        });
    }
    if (findingSeverity(findings, "blocking")) {
        const blocking = findings.filter((finding) => finding.severity === "blocking");
        return {
            mode: "rejected",
            usedKeySource: null,
            statement: blocking[0]?.message ?? "This pack was rejected by the trust root.",
            findings,
        };
    }
    if (findingSeverity(findings, "warning")) {
        const warnings = findings.filter((finding) => finding.severity === "warning");
        return {
            mode: "degraded",
            usedKeySource,
            statement: warnings[0]?.message ?? "This pack was verified in degraded mode.",
            findings,
        };
    }
    return {
        mode: "anchored",
        usedKeySource,
        statement: signatureAlgorithm === "ed25519"
            ? `This pack is anchored: signing key ${signingKeyId} was confirmed against an Ed25519 public-key fingerprint obtained out of band, and the verifier was vouched for independently.`
            : `This pack is anchored: signing key ${signingKeyId} was confirmed against a fingerprint obtained out of band, and the verifier was vouched for independently.`,
        findings,
    };
}
